import { plainToInstance } from 'class-transformer';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import nookies from 'nookies';

import AdBanner from '@/components/AdBanner';
import CurrentForecastWidget from '@/components/CurrentForecastWidget';
import FAQSchema from '@/components/faq/FAQSchema';
import FAQWidget from '@/components/faq/FAQWidget';
import OtherPlacesForecastWidget from '@/components/OtherPlacesForecastWidget';
import QuarteredForecastWidget from '@/components/QuarteredForecastWidget';
import { SAVED_PLACE_COOKIE } from '@/contexts/SavedPlaceContext';
import { BannerEnum } from '@/data/banners';
import Meta from '@/layouts/Meta';
import { CurrentForecast } from '@/models/CurrentForecast';
import { HourlyForecast } from '@/models/HourlyForecast';
import { OtherPlaceForecast } from '@/models/OtherPlaceForecast';
import { QuarteredForecast } from '@/models/QuarteredForecast';
import { getAllForecasts } from '@/providers/ForecastProvider';
import Main from '@/templates/Main';
import NpsWidgetScript, { NpsTypes } from '@/templates/NpsWidgetScript';
import { AppConfig } from '@/utils/AppConfig';
import { generateFAQData } from '@/utils/faq/FaqFormatter';
import { completeSeoData, SeoData } from '@/utils/Seo';
import { SeoPlaceholders } from '@/utils/SeoPlaceholders';

import type { AllForecastsJsonType } from '@/providers/ForecastProvider';

type IProps = {
  forecasts: AllForecastsJsonType;
  seo: SeoData;
  cityVariation: string;
};

const rawSeoData: SeoData = {
  title: `Погода ${SeoPlaceholders.CITY_GENITIVE} — точный прогноз на сегодня, завтра и 10 дней`,
  description: `Точный прогноз погоды ${SeoPlaceholders.CITY_GENITIVE}, ${SeoPlaceholders.SUBJECT}. Температура, ветер, осадки, давление и влажность. Почасовой прогноз на сегодня, погода на завтра и на 10 дней.`,
};

export const getServerSideProps: GetServerSideProps<IProps> = async (ctx) => {
  const placeSlug = ctx.params?.place?.toString() ?? 'vladivostok'; // shitty params type
  const place = AppConfig.partner.getPlaceBySlug(placeSlug);

  if (!place) {
    return {
      notFound: true,
    };
  }

  nookies.set(ctx, SAVED_PLACE_COOKIE, placeSlug, { path: AppConfig.basePath });

  return {
    props: {
      forecasts: await getAllForecasts(place),
      seo: completeSeoData(rawSeoData, place),
      cityVariation: place.variation,
    },
  };
};

const PlaceForecast = ({ forecasts, seo, cityVariation }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const currentForecast = plainToInstance(CurrentForecast, forecasts.current);
  const hourlyForecast = plainToInstance(HourlyForecast, forecasts.hourly);
  const quarteredForecast = plainToInstance(QuarteredForecast, forecasts.quartered);
  const otherPlacesForecast = plainToInstance(OtherPlaceForecast, forecasts.other);
  const beforeFooterBanner = AppConfig.partner.banners[BannerEnum.BEFORE_FOOTER];
  const faqData = generateFAQData(currentForecast, quarteredForecast, cityVariation);

  return (
    <Main meta={<Meta title={seo.title} description={seo.description} />}>
      <h1 className="sr-only">Погода {cityVariation}</h1>
      <CurrentForecastWidget currentForecast={currentForecast} hourlyForecast={hourlyForecast} />
      <QuarteredForecastWidget quarteredForecast={quarteredForecast} />
      <OtherPlacesForecastWidget otherPlacesForecast={otherPlacesForecast} />
      <FAQSchema faqData={faqData} />
      <FAQWidget faqData={faqData} />
      {AppConfig.partner.isDvHab() && (
        <AdBanner
          dataId={beforeFooterBanner.id}
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className={`max-w-[${beforeFooterBanner.width}] mx-auto mt-6 overflow-hidden`}
        />
      )}
      <NpsWidgetScript type={NpsTypes.QUESTION} slug={'vl_weather_main'} />
    </Main>
  );
};

export default PlaceForecast;
